import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SESSION_APP_FALLBACK_TOKEN,
  isSessionAppFallbackHost,
  getSessionLandingPath,
} from '../config/sessionAppData';
import {
  HAIR_APP_SESSION_TOKEN_KEY,
  HAIR_SESSION_HOME_PATH_KEY,
} from '../config/hairApp';

const STORAGE_KEY = HAIR_APP_SESSION_TOKEN_KEY;
/** Persists `/hair/session/...` when the user arrived with a token in the URL (survives refresh on other routes). */
const HOME_PATH_KEY = HAIR_SESSION_HOME_PATH_KEY;

const HairSessionContext = createContext(null);

function readInitialPathHomeFromSessionUrl() {
  if (typeof window === 'undefined') return null;
  const pathname = window.location.pathname;
  const m = /^\/hair\/session\/([^/]+)/i.exec(pathname);
  if (m && m[1]) return pathname;

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const homePath = sessionStorage.getItem(HOME_PATH_KEY);
    if (!stored || !homePath || !homePath.startsWith('/hair/session/')) return null;
    const seg = decodeURIComponent(homePath.slice('/hair/session/'.length).split('/')[0]);
    if (seg === String(stored).trim()) return homePath;
  } catch (_) {}
  return null;
}

export function HairSessionProvider({ children }) {
  const location = useLocation();
  const [pathHomeFromSessionUrl, setPathHomeFromSessionUrl] = useState(readInitialPathHomeFromSessionUrl);
  const [token, setTokenState] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  });

  const setToken = useCallback((value) => {
    const next = typeof value === 'string' ? value : '';
    setTokenState(next);
    if (!next) {
      setPathHomeFromSessionUrl(null);
      try {
        sessionStorage.removeItem(HOME_PATH_KEY);
      } catch (_) {}
    }
    try {
      if (next) {
        sessionStorage.setItem(STORAGE_KEY, next);
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (_) {}
  }, []);

  const sessionLandingPath = useMemo(() => {
    if (pathHomeFromSessionUrl) return pathHomeFromSessionUrl;
    return getSessionLandingPath(token);
  }, [pathHomeFromSessionUrl, token]);

  useEffect(() => {
    const match = /^\/hair\/session\/([^/]+)/i.exec(location.pathname);
    if (match && match[1]) {
      const fromPath = decodeURIComponent(match[1]);
      if (fromPath) {
        setToken(fromPath);
        setPathHomeFromSessionUrl(location.pathname);
        try {
          sessionStorage.setItem(HOME_PATH_KEY, location.pathname);
        } catch (_) {}
      }
      return;
    }
    let stored = '';
    try {
      stored = sessionStorage.getItem(STORAGE_KEY) || '';
    } catch (_) {}
    if (stored.trim()) return;
    if (!isSessionAppFallbackHost()) return;
    setTokenState((prev) => {
      if (prev && prev.trim()) return prev;
      try {
        sessionStorage.setItem(STORAGE_KEY, SESSION_APP_FALLBACK_TOKEN);
      } catch (_) {}
      return SESSION_APP_FALLBACK_TOKEN;
    });
  }, [location.pathname, setToken]);

  return (
    <HairSessionContext.Provider value={{ token, setToken, sessionLandingPath }}>
      {children}
    </HairSessionContext.Provider>
  );
}

export function useHairSession() {
  const ctx = useContext(HairSessionContext);
  if (!ctx) {
    throw new Error('useHairSession must be used within HairSessionProvider');
  }
  return ctx;
}
